import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ReauthRequiredError } from "@/hooks/use-reauth";
import { ApiError, apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { changeOrderKeys, commitmentKeys, payAppKeys } from "@/lib/query";

export interface ChangeOrderLineItem {
  id: string;
  commitmentId: string;
  itemNo: string;
  description: string;
  scheduledValue: string;
  costCodeId: string | null;
  sourceChangeOrderId: string | null;
  retentionPercentOverride: string | null;
}

export interface ChangeOrder {
  id: string;
  orgId: string | null;
  projectId: string;
  commitmentNumber: string;
  commitmentType: string;
  title: string;
  description: string | null;
  status: string;
  originalAmount: string;
  approvedChangesAmount: string;
  revisedAmount: string | null;
  pendingChangesAmount: string;
  retentionPercent: string;
  companyId: string | null;
  companyName: string | null;
  parentCommitmentId: string | null;
  // PCO linkage fields (US-125 / WI-037a).
  pcoLinkageType: "commitment" | "cost_code" | null;
  pcoCostCodeId: string | null;
  pcoRequiresBuyout: boolean;
  scheduleImpactDays: number | null;
  clientDecisionDeadline: string | null;
  retentionPercentOverride: string | null;
  submittedAt: string | null;
  submittedByUserId: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  rejectedAt: string | null;
  rejectedByUserId: string | null;
  rejectionReason: string | null;
  voidedAt: string | null;
  voidedByUserId: string | null;
  voidReason: string | null;
  reconciliationFlag: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChangeOrderDetail extends ChangeOrder {
  lineItems: ChangeOrderLineItem[];
  parent: ChangeOrder | null;
  auditTrail: Array<{
    id: string;
    action: string;
    actorUserId: string | null;
    createdAt: string;
    metadata: Record<string, unknown> | null;
    previousState: Record<string, unknown> | null;
    newState: Record<string, unknown> | null;
  }>;
}

export interface ChangeOrderLineItemInput {
  itemNo?: string;
  description: string;
  scheduledValue: string;
  costCodeId?: string | null;
}

/**
 * Linkage shape mirrored from the server. `commitment` PCOs are tied to a
 * parent commitment (existing flow). `cost_code` PCOs are unforeseen-condition
 * COs that allocate against a cost code (US-125 / WI-037a).
 */
export type PcoLinkagePayload =
  | { linkageType: "commitment"; parentCommitmentId: string }
  | {
      linkageType: "cost_code";
      costCodeId: string;
      selectedCompanyId?: string | null;
      newVendor?: boolean;
    };

export interface CreateChangeOrderPayload {
  projectId: string;
  title: string;
  description?: string;
  scheduleImpactDays?: number | null;
  clientDecisionDeadline?: string | null;
  retentionPercentOverride?: string | null;
  lineItems: ChangeOrderLineItemInput[];
  linkage: PcoLinkagePayload;
}

export interface UpdateChangeOrderPayload {
  title?: string;
  description?: string;
  scheduleImpactDays?: number | null;
  clientDecisionDeadline?: string | null;
  retentionPercentOverride?: string | null;
  lineItems?: ChangeOrderLineItemInput[];
  linkage?: PcoLinkagePayload;
}

export type AppendTargetPayload = { mode: "existing"; commitmentId: string } | { mode: "new" };

export interface AppendTargetOption {
  commitmentId: string;
  commitmentNumber: string;
  commitmentTitle: string;
  revisedAmount: string | null;
  status: string;
}

export interface CommittedSubOption {
  companyId: string;
  companyName: string;
  commitmentId: string;
  commitmentNumber: string;
  commitmentTitle: string;
}

export interface PendingBuyoutRow {
  id: string;
  commitmentNumber: string;
  title: string;
  status: string;
  originalAmount: string;
  scheduleImpactDays: number | null;
  clientDecisionDeadline: string | null;
  pcoCostCodeId: string | null;
  costCode: string | null;
  costCodeDescription: string | null;
  createdAt: string;
}

export function useChangeOrdersForProject(projectId: string, statuses?: readonly string[]) {
  const params = new URLSearchParams();
  params.set("projectId", projectId);
  if (statuses) for (const s of statuses) params.append("status", s);

  return useQuery({
    queryKey: changeOrderKeys.project(projectId, statuses),
    queryFn: () => apiFetch<ChangeOrder[]>(`/change-orders?${params.toString()}`),
    enabled: !!projectId,
  });
}

export function useChangeOrder(id: string | null) {
  return useQuery({
    queryKey: id ? changeOrderKeys.detail(id) : ["change-orders", "none"],
    queryFn: () => apiFetch<ChangeOrderDetail>(`/change-orders/${id}`),
    enabled: !!id,
  });
}

export function useChangeOrdersForCommitment(parentId: string | null) {
  return useQuery({
    queryKey: parentId ? changeOrderKeys.forCommitment(parentId) : ["change-orders", "none"],
    queryFn: () => apiFetch<ChangeOrder[]>(`/erp-commitments/${parentId}/change-orders`),
    enabled: !!parentId,
  });
}

export interface ChangeOrderSovContributor {
  id: string;
  commitmentNumber: string;
  title: string;
  approvedAt: string | null;
  parentCommitmentId: string | null;
  amount: number;
  lineDescription: string;
}

export type ChangeOrderSovRollup = Record<
  string,
  { total: number; contributors: ChangeOrderSovContributor[] }
>;

export function useChangeOrderSovRollup(projectId: string) {
  return useQuery({
    queryKey: ["change-orders", "sov-rollup", projectId] as const,
    queryFn: () => apiFetch<ChangeOrderSovRollup>(`/projects/${projectId}/change-order-sov-rollup`),
    enabled: !!projectId,
  });
}

function invalidateAllCoQueries(qc: ReturnType<typeof useQueryClient>, parentId?: string | null) {
  qc.invalidateQueries({ queryKey: changeOrderKeys.all });
  qc.invalidateQueries({ queryKey: commitmentKeys.all });
  if (parentId) {
    qc.invalidateQueries({ queryKey: commitmentKeys.detail(parentId) });
    qc.invalidateQueries({ queryKey: commitmentKeys.changeOrders(parentId) });
    qc.invalidateQueries({ queryKey: changeOrderKeys.forCommitment(parentId) });
    qc.invalidateQueries({ queryKey: payAppKeys.sub });
  }
}

function parentIdFromLinkage(linkage?: PcoLinkagePayload): string | null | undefined {
  if (!linkage) return undefined;
  return linkage.linkageType === "commitment" ? linkage.parentCommitmentId : null;
}

export function useCreateChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChangeOrderPayload) =>
      apiPost<{ data: ChangeOrder }>("/change-orders", data),
    onSuccess: (_res, vars) => {
      invalidateAllCoQueries(qc, parentIdFromLinkage(vars.linkage));
      toast.success("PCO created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateChangeOrderPayload) =>
      apiPatch<{ data: ChangeOrder }>(`/change-orders/${id}`, data),
    onSuccess: (_res, vars) => {
      invalidateAllCoQueries(qc, parentIdFromLinkage(vars.linkage));
      qc.invalidateQueries({ queryKey: changeOrderKeys.detail(vars.id) });
      toast.success("PCO updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/change-orders/${id}`),
    onSuccess: () => {
      invalidateAllCoQueries(qc);
      toast.success("PCO deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSubmitChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ data: ChangeOrder }>(`/change-orders/${id}/submit`),
    onSuccess: (res) => {
      invalidateAllCoQueries(qc, res.data?.parentCommitmentId);
      if (res.data) qc.invalidateQueries({ queryKey: changeOrderKeys.detail(res.data.id) });
      toast.success("Submitted for approval");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useApproveChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      appendTarget,
      reauthToken,
    }: {
      id: string;
      appendTarget?: AppendTargetPayload;
      reauthToken?: string;
    }) => {
      try {
        return await apiPost<{ data: ChangeOrder }>(
          `/change-orders/${id}/approve`,
          { appendTarget },
          reauthToken ? { "X-Reauth-Token": reauthToken } : undefined,
        );
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 401 &&
          (err.body as { reauthRequired?: boolean } | null)?.reauthRequired
        ) {
          throw new ReauthRequiredError();
        }
        throw err;
      }
    },
    onSuccess: (res) => {
      invalidateAllCoQueries(qc, res.data?.parentCommitmentId);
      if (res.data) qc.invalidateQueries({ queryKey: changeOrderKeys.detail(res.data.id) });
      toast.success(`Approved as ${res.data?.commitmentNumber ?? "CO"}`);
    },
    onError: (err: Error) => {
      // ReauthRequiredError is handled by the caller (opens ReauthDialog); don't toast.
      if (err instanceof ReauthRequiredError) return;
      toast.error(err.message);
    },
  });
}

/**
 * Subs already committed under a cost code on a project. Powers the sub
 * picker shown after the PM picks a cost code in the unforeseen-PCO flow.
 */
export function useCommittedSubsForCostCode(
  projectId: string | null | undefined,
  costCodeId: string | null | undefined,
) {
  return useQuery({
    queryKey: ["change-orders", "committed-subs", projectId, costCodeId] as const,
    queryFn: () =>
      apiFetch<CommittedSubOption[]>(
        `/projects/${projectId}/cost-codes/${costCodeId}/committed-subs`,
      ),
    enabled: !!projectId && !!costCodeId,
  });
}

/**
 * Append-target options for the approval picker on cost-code-linked PCOs.
 * Returns the sub's existing commitments so the approver can choose where
 * the CO line items land (or "Create new" via the UI fallback).
 */
export function useAppendTargetsForChangeOrder(coId: string | null | undefined) {
  return useQuery({
    queryKey: ["change-orders", "append-targets", coId] as const,
    queryFn: () => apiFetch<AppendTargetOption[]>(`/change-orders/${coId}/append-targets`),
    enabled: !!coId,
  });
}

/**
 * Pending-buyout PCOs for the project's Buy Log surface (AC #10).
 */
export function usePendingBuyouts(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ["change-orders", "pending-buyouts", projectId] as const,
    queryFn: () => apiFetch<PendingBuyoutRow[]>(`/projects/${projectId}/pending-buyouts`),
    enabled: !!projectId,
  });
}

export function useRejectChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<{ data: ChangeOrder }>(`/change-orders/${id}/reject`, { reason }),
    onSuccess: (res, { id }) => {
      invalidateAllCoQueries(qc, res.data?.parentCommitmentId);
      qc.invalidateQueries({ queryKey: changeOrderKeys.detail(id) });
      toast.success("PCO rejected");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useVoidChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<{ data: ChangeOrder }>(`/change-orders/${id}/void`, { reason }),
    onSuccess: (res, { id }) => {
      invalidateAllCoQueries(qc, res.data?.parentCommitmentId);
      qc.invalidateQueries({ queryKey: changeOrderKeys.detail(id) });
      toast.success("Change order voided");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
